/* eslint-disable */
/* ---------- THE FRONTIER — 5 coolest things Claude can do right now ----------
   Exports: ClaudeFrontier
   An educational showcase of 5 cutting-edge Claude capabilities, each with a
   self-contained CSS mini-demo in the operator-terminal aesthetic. Positions
   the operator at the frontier; subtly reinforces "this is the stack I build on."

   Specs are accurate as of the Claude API reference (Opus 4.8 = claude-opus-4-8,
   1M context, adaptive thinking with effort low→max, Managed Agents, MCP,
   Computer Use). No invented capabilities.

   WHY HTML/CSS: every label is a reflowing DOM box (word-break + clamp), so text
   can never escape a card border. Animations are pure CSS keyframes.
   prefers-reduced-motion: animations freeze to a sensible resting state.

   REVERSIBLE: gated behind the `showFrontier` tweak (toggle off in the panel)
   and shipped as an isolated commit (git revert removes it cleanly).
   --------------------------------------------------------------------------- */

import React, { useEffect } from 'react';

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById('kz-frontier-styles')) return;
  const s = document.createElement('style');
  s.id = 'kz-frontier-styles';
  s.textContent = `
    .cf-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-top:24px; }
    @media(max-width:900px){ .cf-grid{ grid-template-columns:repeat(2,1fr); } }
    @media(max-width:560px){ .cf-grid{ grid-template-columns:1fr; } }
    .cf-card{
      border:1px solid var(--line-strong); background:var(--bg-1);
      display:flex; flex-direction:column; min-width:0; overflow:hidden;
      transition:border-color 160ms ease, transform 160ms ease;
    }
    .cf-card:hover{ border-color:var(--cfc); transform:translateY(-2px); }
    .cf-viz{
      position:relative; height:128px; border-bottom:1px solid var(--line);
      background:
        radial-gradient(120% 90% at 18% 0%, color-mix(in oklch, var(--cfc) 11%, transparent), transparent 60%),
        var(--bg-2);
      overflow:hidden;
    }
    .cf-body{ padding:16px 16px 18px; min-width:0; }
    .cf-kbar{ display:flex; align-items:center; gap:8px; margin-bottom:8px; min-width:0; }
    .cf-ic{ width:18px; height:18px; flex:none; color:var(--cfc); display:inline-flex; align-items:center; justify-content:center; font-size:13px; }
    .cf-name{ font-size:14px; font-weight:700; color:var(--fg); letter-spacing:-0.01em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .cf-tag{ font-size:9px; letter-spacing:0.18em; text-transform:uppercase; color:var(--cfc); margin-left:auto; flex:none; white-space:nowrap; }
    .cf-desc{ font-size:12.5px; line-height:1.55; color:var(--fg-2); overflow-wrap:anywhere; }
    .cf-spec{ margin-top:10px; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; color:var(--fg-4); overflow-wrap:anywhere; }
    .cf-spec b{ color:var(--cfc); font-weight:600; }

    /* ── 1 · COMPUTER USE — a cursor drives a tiny screen ── */
    .cf-screen{ position:absolute; inset:18px 20px; border:1px solid var(--line-strong); background:var(--bg-1); }
    .cf-screen .bar{ height:11px; border-bottom:1px solid var(--line); background:var(--bg-3); display:flex; align-items:center; gap:3px; padding:0 5px; }
    .cf-screen .bar i{ width:4px; height:4px; border-radius:50%; background:var(--fg-5); flex:none; }
    .cf-screen .btn{ position:absolute; left:50%; top:54%; transform:translate(-50%,-50%); border:1px solid var(--cfc); color:var(--cfc); font-size:8px; padding:3px 8px; letter-spacing:0.1em; }
    .cf-cursor{ position:absolute; color:var(--fg); font-size:13px; left:18%; top:30%; animation:cfCursor 3.6s ease-in-out infinite; }
    .cf-cursor::after{ content:''; position:absolute; left:-3px; top:-3px; width:14px; height:14px; border:1px solid var(--cfc); border-radius:50%; opacity:0; animation:cfClick 3.6s ease-in-out infinite; }
    @keyframes cfCursor{ 0%{left:18%;top:30%;} 40%{left:46%;top:48%;} 55%{left:46%;top:48%;} 60%{left:47%;top:49%;} 100%{left:18%;top:30%;} }
    @keyframes cfClick{ 0%,52%{opacity:0;transform:scale(0.4);} 58%{opacity:1;transform:scale(1);} 70%,100%{opacity:0;transform:scale(1.8);} }

    /* ── 2 · 1M CONTEXT — bar fills + counter ── */
    .cf-ctx{ position:absolute; inset:0; display:flex; flex-direction:column; justify-content:center; padding:0 22px; gap:10px; }
    .cf-ctx .num{ font-family:var(--mono); font-weight:700; font-size:26px; letter-spacing:-0.03em; color:var(--cfc); }
    .cf-ctx .num small{ color:var(--fg-4); font-size:12px; font-weight:400; letter-spacing:0; }
    .cf-track{ height:8px; background:var(--bg-3); border:1px solid var(--line); overflow:hidden; }
    .cf-track > i{ display:block; height:100%; background:var(--cfc); width:8%; animation:cfFill 4s ease-in-out infinite; }
    @keyframes cfFill{ 0%{width:6%;} 55%,80%{width:100%;} 100%{width:6%;} }
    .cf-ctx .lab{ font-size:9px; letter-spacing:0.18em; text-transform:uppercase; color:var(--fg-4); display:flex; justify-content:space-between; }

    /* ── 3 · EXTENDED THINKING — reasoning lines + effort dial ── */
    .cf-think{ position:absolute; inset:0; padding:14px 18px; display:flex; flex-direction:column; gap:5px; }
    .cf-tl{ height:6px; background:color-mix(in oklch, var(--cfc) 45%, var(--bg-3)); opacity:0; animation:cfThink 3.4s ease-in-out infinite; }
    .cf-tl:nth-child(1){ width:64%; animation-delay:0s; }
    .cf-tl:nth-child(2){ width:82%; animation-delay:0.45s; }
    .cf-tl:nth-child(3){ width:48%; animation-delay:0.9s; }
    .cf-tl:nth-child(4){ width:72%; animation-delay:1.35s; }
    @keyframes cfThink{ 0%{opacity:0;transform:translateX(-6px);} 14%,70%{opacity:0.9;transform:none;} 100%{opacity:0;} }
    .cf-dial{ display:flex; align-items:flex-end; gap:3px; margin-top:8px; height:22px; }
    .cf-dial i{ width:7px; background:var(--cfc); opacity:0.3; animation:cfDial 3.4s ease-in-out infinite; }
    .cf-dial i:nth-child(1){ height:30%; animation-delay:0s; }
    .cf-dial i:nth-child(2){ height:52%; animation-delay:0.2s; }
    .cf-dial i:nth-child(3){ height:74%; animation-delay:0.4s; }
    .cf-dial i:nth-child(4){ height:100%; animation-delay:0.6s; }
    .cf-dial span{ align-self:center; margin-left:6px; font-size:8px; letter-spacing:0.14em; text-transform:uppercase; color:var(--fg-4); }
    @keyframes cfDial{ 0%,100%{opacity:0.3;} 50%{opacity:1;} }

    /* ── 4 · MCP — hub with spokes lighting up ── */
    .cf-mcp{ position:absolute; inset:0; }
    .cf-mcp svg{ width:100%; height:100%; display:block; }
    .cf-spoke{ stroke:var(--cfc); stroke-width:1; stroke-dasharray:3 4; opacity:0.5; animation:cfDash 1.2s linear infinite; }
    @keyframes cfDash{ to{ stroke-dashoffset:-7; } }
    .cf-node{ fill:var(--bg-1); stroke:var(--cfc); stroke-width:1; }
    .cf-hub{ fill:color-mix(in oklch, var(--cfc) 22%, var(--bg-1)); stroke:var(--cfc); stroke-width:1.5; }
    .cf-nlabel{ font-family:var(--mono); font-size:7px; fill:var(--fg-3); }

    /* ── 5 · AGENTS — loop with a moving dot ── */
    .cf-loop{ position:absolute; inset:0; }
    .cf-loop svg{ width:100%; height:100%; display:block; }
    .cf-ring{ fill:none; stroke:var(--line-strong); stroke-width:1; }
    .cf-ring-on{ fill:none; stroke:var(--cfc); stroke-width:1.5; stroke-dasharray:6 6; opacity:0.7; animation:cfDash2 1s linear infinite; }
    @keyframes cfDash2{ to{ stroke-dashoffset:-12; } }
    .cf-orbit{ offset-path: circle(34px at 50% 50%); animation:cfOrbit 3s linear infinite; }
    @keyframes cfOrbit{ to{ offset-distance:100%; } }
    .cf-llabel{ font-family:var(--mono); font-size:7px; fill:var(--fg-3); text-anchor:middle; }

    @media (prefers-reduced-motion: reduce){
      .cf-cursor, .cf-cursor::after, .cf-track > i, .cf-tl, .cf-dial i, .cf-spoke, .cf-ring-on, .cf-orbit{ animation:none !important; }
      .cf-track > i{ width:100%; } .cf-tl{ opacity:0.9; } .cf-dial i{ opacity:0.8; }
    }
  `;
  document.head.appendChild(s);
}

const CAPS = [
  {
    id: 'computer', c: '#5FBFB9', ic: '⌖', name: 'Computer Use', tag: 'sees + acts',
    desc: 'Claude looks at a screen and drives the mouse and keyboard — clicking, typing, navigating real software no API exposes.',
    spec: <>vision + action · <b>server or self-hosted</b></>,
    viz: (
      <div className="cf-screen">
        <div className="bar"><i /><i /><i /></div>
        <div className="btn">submit</div>
        <span className="cf-cursor">▲</span>
      </div>
    ),
  },
  {
    id: 'context', c: '#F5A623', ic: '▦', name: '1M-token context', tag: 'total recall',
    desc: 'A million tokens in a single prompt. Whole codebases, entire archives, a year of docs — reasoned over at once, nothing chunked away.',
    spec: <>Opus 4.8 · Sonnet 4.6 · <b>Fable 5</b></>,
    viz: (
      <div className="cf-ctx">
        <div className="num">1,000,000 <small>tokens</small></div>
        <div className="cf-track"><i /></div>
        <div className="lab"><span>1 prompt</span><span>≈ 1500 pages</span></div>
      </div>
    ),
  },
  {
    id: 'thinking', c: '#c98ec0', ic: '◇', name: 'Extended thinking', tag: 'reasons first',
    desc: 'Claude works the problem step-by-step before it answers — and you dial how hard it thinks, from a quick pass to maximum effort.',
    spec: <>adaptive thinking · <b>effort low → max</b></>,
    viz: (
      <div className="cf-think">
        <div className="cf-tl" /><div className="cf-tl" /><div className="cf-tl" /><div className="cf-tl" />
        <div className="cf-dial"><i /><i /><i /><i /><span>effort</span></div>
      </div>
    ),
  },
  {
    id: 'mcp', c: '#00DC82', ic: '⬡', name: 'MCP', tag: 'plugs into anything',
    desc: 'One open protocol — the Model Context Protocol — connects Claude to any tool, database, or service. Build once, works everywhere.',
    spec: <>model context protocol · <b>open standard</b></>,
    viz: (
      <div className="cf-mcp">
        <svg viewBox="0 0 260 128" aria-hidden="true">
          <line className="cf-spoke" x1="130" y1="64" x2="48" y2="30" />
          <line className="cf-spoke" x1="130" y1="64" x2="44" y2="98" style={{ animationDelay: '0.2s' }} />
          <line className="cf-spoke" x1="130" y1="64" x2="214" y2="28" style={{ animationDelay: '0.4s' }} />
          <line className="cf-spoke" x1="130" y1="64" x2="218" y2="100" style={{ animationDelay: '0.6s' }} />
          <rect className="cf-node" x="22" y="22" width="40" height="16" /><text className="cf-nlabel" x="42" y="33" textAnchor="middle">gmail</text>
          <rect className="cf-node" x="18" y="90" width="44" height="16" /><text className="cf-nlabel" x="40" y="101" textAnchor="middle">slack</text>
          <rect className="cf-node" x="196" y="20" width="40" height="16" /><text className="cf-nlabel" x="216" y="31" textAnchor="middle">postgres</text>
          <rect className="cf-node" x="196" y="92" width="44" height="16" /><text className="cf-nlabel" x="218" y="103" textAnchor="middle">github</text>
          <rect className="cf-hub" x="108" y="54" width="44" height="20" /><text className="cf-nlabel" x="130" y="67" textAnchor="middle" style={{ fill: 'var(--fg)' }}>claude</text>
        </svg>
      </div>
    ),
  },
  {
    id: 'agents', c: '#FF6B47', ic: '◆', name: 'Agents', tag: 'run their own loop',
    desc: 'Agents that plan, call tools, read the result, and decide the next move — looping until the job is done. The stack behind my agent fleet.',
    spec: <>agent sdk · managed agents · <b>mcp + skills</b></>,
    viz: (
      <div className="cf-loop">
        <svg viewBox="0 0 260 128" aria-hidden="true">
          <circle className="cf-ring" cx="130" cy="64" r="34" />
          <circle className="cf-ring-on" cx="130" cy="64" r="34" />
          <text className="cf-llabel" x="130" y="36">plan</text>
          <text className="cf-llabel" x="172" y="67">act</text>
          <text className="cf-llabel" x="130" y="100">observe</text>
          <text className="cf-llabel" x="88" y="67">loop</text>
          <rect className="cf-orbit" x="-3" y="-3" width="6" height="6" fill="var(--cfc)" />
        </svg>
      </div>
    ),
  },
];

function ClaudeFrontier() {
  useEffect(() => { injectStyles(); }, []);
  return (
    <section data-section="frontier" className="section-pad" style={{ paddingTop: 28 }}>
      <div className="container">
        <div className="box-head">
          <div className="rule" />
          <span>the frontier · what Claude can actually do right now</span>
          <div className="rule right" />
        </div>

        <h2 className="h-section" style={{ marginBottom: 8 }}>
          I build on the bleeding edge.
          <br />
          <span className="dim2" style={{ fontWeight: 600 }}>{'>'} five things Claude can do today that most people don't know exist.</span>
        </h2>

        <div className="cf-grid">
          {CAPS.map((cap) => (
            <div className="cf-card" key={cap.id} style={{ '--cfc': cap.c }}>
              <div className="cf-viz">{cap.viz}</div>
              <div className="cf-body">
                <div className="cf-kbar">
                  <span className="cf-ic">{cap.ic}</span>
                  <span className="cf-name">{cap.name}</span>
                  <span className="cf-tag">{cap.tag}</span>
                </div>
                <p className="cf-desc">{cap.desc}</p>
                <div className="cf-spec">{cap.spec}</div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-16" style={{ fontSize: 12, color: 'var(--fg-3)', maxWidth: '72ch', lineHeight: 1.6 }}>
          None of this is a demo reel — it's the toolkit I run my agent fleet on, today, on Claude Opus 4.8.
          The gap between "AI is coming" and "AI is doing my work" is just knowing these exist and wiring them up.{' '}
          <a href="https://www.readyiq.ai/book" target="_blank" rel="noreferrer" style={{ color: '#a78bfa', borderBottom: '1px dashed rgba(124,58,237,0.6)' }}>ReadyIQ wires them up for you →</a>
        </p>
      </div>
    </section>
  );
}

Object.assign(window, { ClaudeFrontier });

export { ClaudeFrontier };
